import { v4 as uuid } from "uuid";
import { Module } from "vuex";

import AdvertisersApi from "@/api/AdvertisersApi";
import AgenciesApi from "@/api/AgenciesApi";
import ProjectsApi from "@/api/ProjectsApi";
import { RootState } from "@/store";
import { AdvertiserListEntity, AgencyListEntity, Project } from "@/types/plans";

export interface BusinessGoal {
  id: string;
  title: string;
  campaignId?: string;
  b2bb2c?: string;
}

const LS_JOBS_KEY = "generation_jobs";

export interface AdvertiserB2 {
  pixeling_experience?: number | string;
  website_conversion_experience?: number | string;
  average_order_value?: number | string;
  brand_recognition?: number | string;
  estimated_time_to_conversion?: number | string;
  estimated_audience_size?: number | string;
}

export interface Advertiser {
  id: string;
  name: string;
  industry?: string;
  sub_industry?: string;
  url?: string;
  b2b?: AdvertiserB2 | null;
  b2c?: AdvertiserB2 | null;
  agency_id?: string;
}

export interface PlanCreateState {
  businessGoals: BusinessGoal[];
  advertisers: Advertiser[];
  projects: Project[];
  loading: boolean;
  error: string | null;
  selectedAdvertiser: AdvertiserListEntity | null;
  selectedAdvertiserDetailed: Advertiser | null;
  agencies: AgencyListEntity[];
  selectedAgency: AgencyListEntity | null;
  selectedProject: string | null;
  localJobs: LocalJobRecord[];
}

const state: PlanCreateState = {
  businessGoals: [],
  advertisers: [],
  projects: [],
  loading: false,
  error: null,
  selectedAdvertiser: null,
  selectedAdvertiserDetailed: null,
  agencies: [],
  selectedAgency: null,
  selectedProject: null,
  localJobs: JSON.parse(localStorage.getItem(LS_JOBS_KEY) || "[]"),
};

export interface AudienceEstimateParams {
  states?: string[];
  cities?: string[];
  dma?: string[];
  zip_codes?: (number | string)[];
  gender: string[];
  ages: string[];
}

export interface LocalJobRecord {
  projectId: string;
  projectName: string;
  scenarioId: string;
  scenarioName: string;
  jobId: string;
  date: string;
  status: string;
}

const planCreate: Module<PlanCreateState, RootState> = {
  namespaced: true,

  state,

  mutations: {
    addLocalJobs(state, jobs: LocalJobRecord[]) {
      state.localJobs.push(...jobs);
      try {
        localStorage.setItem(LS_JOBS_KEY, JSON.stringify(state.localJobs));
      } catch (e) {
        console.error("[planCreate/addLocalJobs] failed to persist to localStorage", e);
      }
    },

    addBusinessGoal(state, goal: BusinessGoal): void {
      state.businessGoals.push(goal);
    },
    addAdvertiser(state, advertiser: Advertiser): void {
      state.advertisers.push(advertiser);
    },
    addProject(state, project: Project): void {
      state.projects.push(project);
    },
    setProjectList(state, data: Project[]) {
      state.projects = data;
    },
    setAdvertisersList(state, data: AdvertiserListEntity[]) {
      state.advertisers = data;
    },
    setLoading(state, loading: boolean): void {
      state.loading = loading;
    },
    setError(state, error: string | null): void {
      state.error = error;
    },
    setSelectedAdvertiser(state, advertiser: AdvertiserListEntity | null): void {
      state.selectedAdvertiser = advertiser;
    },
    setSelectedAdvertiserDetailed(state, advertiser: Advertiser | null): void {
      state.selectedAdvertiserDetailed = advertiser;
    },
    setAgenciesList(state, agencies: AgencyListEntity[]): void {
      state.agencies = agencies;
    },
    setSelectedAgency(state, agency: AgencyListEntity | null): void {
      state.selectedAgency = agency;
    },
    setSelectedProject(state, projectName: string | null): void {
      state.selectedProject = projectName;
    },
    updateLocalJobStatus(state, payload: { jobId: string; scenarioId?: string; status: LocalJobRecord["status"] }) {
      const rows = state.localJobs.filter(
        (r) => r.jobId === payload.jobId && (!payload.scenarioId || r.scenarioId === payload.scenarioId)
      );
      for (const r of rows) r.status = payload.status;
      try {
        localStorage.setItem(LS_JOBS_KEY, JSON.stringify(state.localJobs));
      } catch (e) {
        console.error(e);
      }
    },
  },

  actions: {
    addLocalJobs({ commit }, jobs: LocalJobRecord[]) {
      commit("addLocalJobs", jobs);
    },
    addBusinessGoal({ commit }, goal: Omit<BusinessGoal, "id">): void {
      const newGoal: BusinessGoal = { id: uuid(), ...goal };
      commit("addBusinessGoal", newGoal);
    },
    addAdvertiser({ commit }, advertiser: Omit<Advertiser, "id">): Advertiser {
      const newAdvertiser: Advertiser = { id: uuid(), ...advertiser };
      commit("addAdvertiser", newAdvertiser);
      return newAdvertiser;
    },
    addProject({ commit }, project: Omit<Project, "id">): Project {
      const newProject: Project = { id: uuid(), ...project };
      commit("addProject", newProject);
      return newProject;
    },
    setSelectedAdvertiser({ commit }, advertiser: AdvertiserListEntity | null): void {
      commit("setSelectedAdvertiser", advertiser);
    },

    setSelectedAdvertiserDetailed({ commit }, advertiser: Advertiser | null): void {
      commit("setSelectedAdvertiserDetailed", advertiser);
    },
    async ensureSelectedAdvertiserDetailed({ commit, state }): Promise<Advertiser | null> {
      const existing = state.selectedAdvertiserDetailed;
      if (existing && (existing.b2b || existing.b2c)) return existing;

      const project = state.projects.find((p) => p.name === state.selectedProject);
      if (!project?.id) return existing ?? null;

      try {
        const api = new ProjectsApi();
        // getProjectById response shape is not modelled yet.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const full = (await api.getProjectById<any>(project.id)) ?? {};
        const profile = full?.advertiser_profile ?? null;
        if (!profile) return existing ?? null;

        const detailed = { id: full?.advertiser_id ?? "", ...profile } as Advertiser;
        commit("setSelectedAdvertiserDetailed", detailed);
        return detailed;
      } catch (e) {
        console.error("[planCreate/ensureSelectedAdvertiserDetailed]", e);
        return existing ?? null;
      }
    },
    async estimateAudienceSize({ commit }, params: AudienceEstimateParams): Promise<number> {
      commit("setError", null);

      try {
        const normalizedZipCodes =
          params.zip_codes
            ?.map((z) => (typeof z === "string" ? parseInt(z, 10) : z))
            .filter((z) => Number.isFinite(z as number)) ?? undefined;

        const payload: AudienceEstimateParams = {
          states: params.states,
          cities: params.cities,
          dma: params.dma,
          zip_codes: normalizedZipCodes as number[] | undefined,
          gender: params.gender,
          ages: params.ages,
        };

        const api = new ProjectsApi();
        const res: Record<string, number> = await api.calculateAudienceSize(payload);

        return res.audience_size;
      } catch (e) {
        console.error("[estimateAudienceSize] error", e);
        commit("setError", "Failed to estimate audience size");
        throw e;
      }
    },

    async fetchProjects({ commit }) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const api = new ProjectsApi();
        const response = await api.getAllProjects();
        commit("setProjectList", response);
      } catch (e) {
        commit("setError", "Failed to load projects");
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchAdvertisers({ commit }) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const api = new AdvertisersApi();
        const response = await api.getAllAdvertisers();
        commit("setAdvertisersList", response);
      } catch (e) {
        commit("setError", "Failed to advertisers list");
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchAdvertisersByAgency({ commit }, agencyName: string) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const api = new AdvertisersApi();
        const response = await api.getAdvertisersByAgency(agencyName);
        commit("setAdvertisersList", response);
      } catch (e) {
        commit("setError", "Failed to advertisers list");
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchAdvertisersData({ commit, state }) {
      const advertiser = state.selectedAdvertiser;

      if (!advertiser?.id) {
        commit("setError", "No advertiser selected");
        return;
      }

      commit("setLoading", true);
      commit("setError", null);

      try {
        const api = new AdvertisersApi();
        const response = await api.getAllAdvertiserByID(advertiser.id);
        commit("setSelectedAdvertiserDetailed", response);
      } catch (e) {
        commit("setError", "Failed to load advertiser details");
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchAgencies({ commit }) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const api = new AgenciesApi();
        const response = await api.getAllAgencies();
        commit("setAgenciesList", response);
      } catch (e) {
        commit("setError", "Failed to load agencies");
      } finally {
        commit("setLoading", false);
      }
    },

    async createAdvertiser({ commit, dispatch, state }, advertiser: Advertiser) {
      const agency = state.selectedAgency;

      if (!agency || !agency.id) {
        console.error("No selected agency");
        throw new Error("Agency is required");
      }

      commit("setLoading", true);
      commit("setError", null);

      try {
        const clean = (obj: Advertiser): Advertiser =>
          Object.fromEntries(
            Object.entries(obj)
              .filter(([, v]) => {
                if (v === null || v === "" || v === undefined) return false;
                if (typeof v === "object" && !Array.isArray(v)) {
                  const cleaned = clean(v);
                  return Object.keys(cleaned).length > 0;
                }
                return true;
              })
              .map(([k, v]) => [k, typeof v === "object" && !Array.isArray(v) ? clean(v) : v])
          ) as Advertiser;

        const cleanedAdvertiser = {
          ...clean(advertiser),
          agency_id: agency.id,
        };

        const api = new AdvertisersApi();
        const created = await api.createAdvertiser(cleanedAdvertiser);
        commit("setSelectedAdvertiserDetailed", created);
        await dispatch("fetchAdvertisers");
        return created;
      } catch (e) {
        commit("setError", "Failed to create advertiser");
        throw e;
      } finally {
        commit("setLoading", false);
      }
    },
    async updateAdvertiser({ commit, dispatch }, { id, advertiser }: { id: string; advertiser: Advertiser }) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const clean = (obj: Advertiser): Advertiser =>
          Object.fromEntries(
            Object.entries(obj)
              .filter(([, v]) => {
                if (v === null || v === "" || v === undefined) return false;
                if (typeof v === "object" && !Array.isArray(v)) {
                  const cleaned = clean(v);
                  return Object.keys(cleaned).length > 0;
                }
                return true;
              })
              .map(([k, v]) => [k, typeof v === "object" && !Array.isArray(v) ? clean(v) : v])
          ) as Advertiser;

        const cleanedAdvertiser = {
          ...clean(advertiser),
        };

        const api = new AdvertisersApi();
        const updated = await api.updateAdvertiserById(id, cleanedAdvertiser);
        commit("setSelectedAdvertiserDetailed", updated);
        await dispatch("fetchAdvertisers");
        return updated;
      } catch (e) {
        commit("setError", "Failed to update advertiser");
        throw e;
      } finally {
        commit("setLoading", false);
      }
    },
  },

  getters: {
    businessGoals: (state): BusinessGoal[] => state.businessGoals,
    advertisers: (state): AdvertiserListEntity[] => state.advertisers,
    projects: (state): Project[] => state.projects,
    selectedAdvertiser: (state): AdvertiserListEntity | null => state.selectedAdvertiser,
    selectedAdvertiserDetailed: (state): Advertiser | null => state.selectedAdvertiserDetailed,
    loading: (state): boolean => state.loading,
    agencies: (state): AgencyListEntity[] => state.agencies,
    selectedAgency: (state): AgencyListEntity | null => state.selectedAgency,
    selectedProject: (state): string | null => state.selectedProject,
    localJobs: (state): LocalJobRecord[] => state.localJobs,
  },
};

export default planCreate;
